import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Spinner, Row, Col, Form, Dropdown, Modal, Button } from "react-bootstrap";
import { Input, Empty } from "antd";
import { toast } from "react-toastify";

// Hooks & Common Components
import useFeatureDials from "hooks/feature-dial/useFeatureDial";
import useDialMemberships from "hooks/feature-dial/useDialMemberships";
import useDebounce from "hooks/route-rules/useDebounce";
import paths from "routes/paths";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import IconButton from "components/common/IconButton";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import CardDropdown from "components/common/CardDropdown";

// Advance Table Components
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import TablePaginationFooter from "components/common/TablePaginationFooter";

// Internal Drawer Component (Imported from your other file)
import FeatureDialDrawer from "./FeatureDialDrawer";

const getAppId = (app) =>
  app?.id || app?.appId || app?.applicationId || app?.uuid || app?.key || null;

const getAppLabel = (app) =>
  app?.name ||
  app?.displayName ||
  app?.code ||
  app?.appCode ||
  app?.key ||
  app?.appKey ||
  app?.slug ||
  app?.value ||
  app?.applicationName ||
  app?.id ||
  "App";

/* ==========================================================================
   FEATURE DIALS TABLE COMPONENT
   ========================================================================== */
const FeatureDialsTable = ({
  data,
  onEdit,
  onToggle,
  setShowDeleteModal,
  setDialToDelete,
  page,
  pageSize,
  totalPages,
  totalElements,
  setPage,
  setPageSize,
}) => {
  const confirmDelete = (id) => {
    setDialToDelete(id);
    setShowDeleteModal(true);
  };

  const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: "Feature",
      cell: ({ row }) => {
        const { name } = row.original;
        return (
          <div className="py-1">
            <h6 className="mb-0 text-900 fs-10">{name}</h6>
          </div>
        );
      },
      meta: { headerProps: { className: "text-900" } }
    },
    {
      id: "apps",
      header: "Apps",
      cell: ({ row }) => {
        const apps = row.original.applications || [];
        if (!apps.length) {
          return <span className="text-500 fs-11">---</span>;
        }
        const visible = apps.slice(0, 3);
        const extra = apps.length - visible.length;
        return (
          <div className="d-flex flex-wrap gap-1">
            {visible.map((app, idx) => {
              const label = getAppLabel(app);
              const pillThemes = [
                { bg: "#e0f2fe", border: "#7dd3fc", text: "#0c4a6e" },
                { bg: "#dcfce7", border: "#86efac", text: "#14532d" },
                { bg: "#fef3c7", border: "#fcd34d", text: "#78350f" },
              ];
              const theme = pillThemes[idx % pillThemes.length];
              return (
                <span
                  key={app?.id || `${label}-${idx}`}
                  className="badge rounded-pill"
                  style={{
                    fontSize: "0.65rem",
                    backgroundColor: theme.bg,
                    border: `1px solid ${theme.border}`,
                    color: theme.text,
                    fontWeight: 600,
                  }}
                >
                  {label}
                </span>
              );
            })}
            {extra > 0 && (
              <span
                className="badge rounded-pill"
                style={{
                  fontSize: "0.65rem",
                  backgroundColor: "#f1f5f9",
                  border: "1px solid #cbd5e1",
                  color: "#334155",
                  fontWeight: 600,
                }}
              >
                +{extra} more
              </span>
            )}
          </div>
        );
      },
      meta: { headerProps: { className: "text-900" } }
    },
    {
      accessorKey: "isOn",
      header: "Active Status",
      cell: ({ row }) => (
        <Form.Check 
          type="switch"
          id={`dial-toggle-${row.original.id}`}
          checked={!!row.original.isOn}
          onChange={() => onToggle(row.original)}
          className="cursor-pointer d-flex justify-content-center"
        />
      ),
      meta: { 
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" } 
      }
    },
    {
      accessorKey: "updatedAt",
      header: "Last Modified",
      cell: ({ row }) => {
        const date = row.original.updatedAt || row.original.lastUpdatedAt;
        return (
          <span className="text-500 d-inline-block fs-11">
            {date ? new Date(date).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : "---"}
          </span>
        );
      },
      meta: { headerProps: { className: "text-900" } },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <CardDropdown drop="start">
          <div className="py-2">
            <Dropdown.Item onClick={() => onEdit(row.original)}>View/edit</Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => confirmDelete(row.original.id)} className="text-danger">
              Delete Dial
            </Dropdown.Item>
          </div>
        </CardDropdown>
      ),
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" }
      }
    }
  ], [onEdit, onToggle]);

  const table = useAdvanceTable({
    data: data || [],
    columns,
    sortable: true,
    pagination: true,
    perPage: pageSize,
  });

  return (
    <AdvanceTableProvider {...table}>
      <div className="mx-n1 px-1">
        <AdvanceTable
          headerClassName="bg-light text-nowrap align-middle text-dark"
          rowClassName="align-middle white-space-nowrap fs-10"
          tableProps={{ bordered: true, striped: true, className: "mb-0" }}
        />
      </div>
      <div className="mt-3">
        <TablePaginationFooter
          page={page}
          totalPages={totalPages || 0}
          totalElements={totalElements || 0}
          onPrev={page > 0 ? () => setPage(page - 1) : undefined}
          onNext={page < (totalPages - 1) ? () => setPage(page + 1) : undefined}
          pageSize={pageSize}
          onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
          pageSizeOptions={[10, 20, 30, 50]}
        />
      </div>
    </AdvanceTableProvider>
  );
};

/* ==========================================================================
   MAIN FEATURE DIALS PAGE
   ========================================================================== */
const FeatureDials = () => {
  const navigate = useNavigate();
  const { dialId } = useParams();
  const location = useLocation();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isToggleSubmitting, setIsToggleSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [dialToDelete, setDialToDelete] = useState(null);
  const [drawerDial, setDrawerDial] = useState(null);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [dialToToggle, setDialToToggle] = useState(null);

  const {
    featureDials = [],
    loading,
    pagination,
    fetchAllFeatureDials,
    searchFeatureDials,
    updateFeatureDial,
    addFeatureDial,
    deleteFeatureDial,
  } = useFeatureDials(page, pageSize);
  const { syncDialApplications } = useDialMemberships();

  const isCreating = location.pathname.endsWith("/add");
  const isEditing = !!dialId && !isCreating;
  const isDrawerOpen = isCreating || isEditing;

  const handleOpenAddDrawer = () => navigate(paths.featureDialAdd);
  const handleOpenEditDrawer = (dial) => {
    if (typeof dial === "object") setDrawerDial(dial);
    navigate(paths.featureDialDetail(dial.id || dial));
  };
  const handleCloseDrawer = () => navigate(paths.featureDial);

  const selectedDial = useMemo(() => {
    return featureDials.find((d) => String(d.id) === String(dialId)) || 
           (drawerDial && String(drawerDial.id) === String(dialId) ? drawerDial : null);
  }, [featureDials, drawerDial, dialId]);

  const stats = useMemo(() => ({
    total: pagination?.totalElements || 0,
    active: featureDials.filter(d => d.isOn).length,
    inactive: featureDials.filter(d => !d.isOn).length,
  }), [featureDials, pagination]);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const trimmed = debouncedSearchTerm.trim();
    if (trimmed.length === 0) {
      fetchAllFeatureDials(page);
    } else {
      searchFeatureDials(trimmed, "ALL", page);
    }
  }, [debouncedSearchTerm, page, fetchAllFeatureDials, searchFeatureDials]);

  const requestToggle = (dial) => {
    setDialToToggle(dial);
    setShowToggleModal(true);
  };

  const handleToggleConfirm = async () => {
    if (!dialToToggle?.id) return;
    setIsToggleSubmitting(true);
    try {
      await updateFeatureDial(dialToToggle.id, { isOn: !dialToToggle.isOn });
      setShowToggleModal(false);
      setDialToToggle(null);
    } catch (err) {
      toast.error("Failed to toggle feature");
    } finally {
      setIsToggleSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsSubmitting(true);
    try {
      await deleteFeatureDial(dialToDelete);
      setShowDeleteModal(false);
    } finally { setIsSubmitting(false); }
  };

  const handleSave = async ({ dialPayload, appIds }) => {
    setIsSubmitting(true);
    try {
      const savedDial = await (
        isCreating ? addFeatureDial(dialPayload) : updateFeatureDial(dialId, dialPayload)
      );

      if (!isCreating) {
        const targetDialId = savedDial?.id || dialId;
        if (targetDialId && Array.isArray(appIds)) {
          const existingApps =
            selectedDial && String(selectedDial.id) === String(targetDialId)
              ? selectedDial.applications || []
              : [];
          const existingIds = (existingApps || [])
            .map(getAppId)
            .filter(Boolean);
          await syncDialApplications(targetDialId, appIds, existingIds);
          await fetchAllFeatureDials(page);
        }
      }
      handleCloseDrawer();
    } finally { setIsSubmitting(false); }
  };

  return (
    <>
      <WidgetSectionTitle
        icon="sliders-h"
        title="Feature Dials"
        subtitle="Manage application feature toggles and configurations."
        className="mb-3 mt-3"
      />

      <div className="p-3 rounded-3 mb-4 d-flex align-items-center justify-content-between flex-wrap bg-light border">
        <div className="d-flex gap-2">
          {[
            { label: "TOTAL", count: stats.total, border: "#6c757d", text: "#495057" },
            { label: "ON", count: stats.active, border: "#10b981", text: "#059669" },
            { label: "OFF", count: stats.inactive, border: "#dc3545", text: "#dc3545" },
          ].map((item) => (
            <div key={item.label} className="p-2 rounded-3 bg-white border-start border-4" style={{ minWidth: 90, borderColor: item.border }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: item.text }}>{item.label}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: item.text }}>{item.count}</div>
            </div>
          ))}
        </div>
        <IconButton
          variant="falcon-default"
          size="sm"
          icon="plus"
          onClick={handleOpenAddDrawer}
          style={{ backgroundColor: "#10b981", color: "#fff", borderColor: "#10b981" }}
        >
          Register Dial
        </IconButton>
      </div>
  {/* ================= SEARCH + FILTERS (Users Styling) ================= */}
      <Row className="mb-2 g-3 align-items-center">
        <Col md={8} lg={9}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Input
              prefix={<span style={{ fontSize: 14 }}>🔍</span>}
              placeholder="Search dials ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              size="large"
              style={{
                borderRadius: 12,
                height: 30,
                flex: "1 1 250px",
                maxWidth: 300
              }}
            />

          </div>
        </Col>

        <Col md={4} lg={3} className="text-end">
          <div style={{ fontSize: 13, color: "#64748b" }}>
            Results: <span className="fw-semibold text-dark">{featureDials.length}</span> of{" "}
            <span className="fw-semibold text-dark">{pagination?.totalElements || 0}</span>
          </div>
        </Col>
      </Row>

      <div className="position-relative" style={{ minHeight: 300 }}>
          {loading && (
                  <div 
                    className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center" 
                    style={{ 
                        zIndex: 10, 
                        backgroundColor: featureDials.length > 0 ? 'rgba(255, 255, 255, 0.6)' : '#fff',
                        minHeight: 400 
                    }}
                  >
                    <div className="text-center">
                        <Spinner animation="border" variant="success" size="sm" />
                        <div className="mt-2 text-500 fs-10 fw-medium">Syncing dials data...</div>
                    </div>
                  </div>
                )}
        {!loading && featureDials.length > 0 ? (
          <FeatureDialsTable
            data={featureDials}
            onEdit={handleOpenEditDrawer}
            onToggle={requestToggle}
            setShowDeleteModal={setShowDeleteModal}
            setDialToDelete={setDialToDelete}
            page={page}
            pageSize={pageSize}
            totalPages={pagination?.totalPages}
            totalElements={pagination?.totalElements}
            setPage={setPage}
            setPageSize={setPageSize}
          />
        ) : !loading && <Empty className="py-5" />}
      </div>

      {isDrawerOpen && (
        <FeatureDialDrawer
          show={isDrawerOpen}
          dial={selectedDial}
          onClose={handleCloseDrawer}
          onSave={handleSave}
          isSubmitting={isSubmitting}
        />
      )}

      <ConfirmDeleteModal
        itemName="feature dial"
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDeleteConfirm}
        loading={isSubmitting}
      />

      <Modal
        show={showToggleModal}
        onHide={() => {
          if (isToggleSubmitting) return;
          setShowToggleModal(false);
          setDialToToggle(null);
        }}
        centered
        size="sm"
      >
        <div className="px-4 py-3" style={{ fontSize: "0.85rem" }}>
          <p className="mb-2 fw-bold">Confirm Status Change</p>
          <div className="mt-0" style={{ fontSize: "0.85rem" }}>
            {dialToToggle ? (
              <>
                Are you sure you want to turn{" "}
                <strong>{dialToToggle.isOn ? "OFF" : "ON"}</strong> this feature dial
                <strong>{` "${dialToToggle.name}"`}</strong>?
              </>
            ) : (
              "Are you sure you want to change this feature dial status?"
            )}
          </div>
          <div className="d-flex align-items-center gap-2 mt-3">
            <Button
              size="sm"
              variant="secondary"
              style={{ fontSize: "0.85rem" }}
              onClick={() => {
                setShowToggleModal(false);
                setDialToToggle(null);
              }}
              disabled={isToggleSubmitting}
            >
              Cancel
            </Button>
            {isToggleSubmitting ? (
              <Spinner animation="border" size="sm" variant="success" />
            ) : (
              <Button
                size="sm"
                variant={dialToToggle?.isOn ? "danger" : "success"}
                style={{ fontSize: "0.85rem" }}
                onClick={handleToggleConfirm}
              >
                {dialToToggle?.isOn ? "Turn Off" : "Turn On"}
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FeatureDials;
